import { Amplify } from 'aws-amplify';
import {
  fetchAuthSession,
  getCurrentUser,
  JWT,
  signInWithRedirect,
  signOut,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import {
  createContext, ReactNode, useEffect, useMemo, useState, useCallback,
} from 'react';
import { useIdleTimer } from 'react-idle-timer';
import {
  authConfig,
  callbackUrl,
  cis2Enabled,
  cis2IdpName,
} from 'auth/configureAmplify.ts';
import LandingPage from 'pages/LandingPage.tsx';
import NoGroupPage from 'components/NoGroupPage/index.tsx';
import { INTENDED_URL } from 'constants/links.ts';
import { isValidInternalPath } from 'utils/validatorUtils.ts';
import { DateTime } from 'luxon';
import ProgressIndicator from 'components/ProgressIndicator';
import {
  Container, SkipLink,
} from 'nhsuk-react-components';
import HomeHeader from 'components/HomeHeader/HomeHeader.tsx';
import PageFooter from 'components/Footer/Footer.tsx';

export const DEFAULT_AUTH_WARNING_OFFSET_MINUTES = 50;

export type AuthContextType = {
  user: {
    username: string;
    userId: string;
  } | null;
  tokens: {
    accessToken: JWT | null;
    idToken: JWT | null;
  };
  login: () => void;
  logout: () => void;
  error: string | null;
  cis2TimeOutWarning: boolean;
};

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | null>(null);

Amplify.configure(authConfig, { ssr: true });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<{ username: string; userId: string }|null>(null);
  const [error, setError] = useState<string|null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSignedIn, setIsSignedIn] = useState<boolean>(false);
  const [hasCognitoGroup, setHasCognitoGroup] = useState<boolean>(false);
  const [idToken, setIdToken] = useState<JWT|null>(null);
  const [accessToken, setAccessToken] = useState<JWT|null>(null);
  const [cis2TimeOutWarning, setCis2TimeOutWarning] = useState(false);

  const warningOffsetMinutes = Number.parseInt(
    import.meta.env.VITE_AUTH_WARNING_OFFSET_MINUTES ?? DEFAULT_AUTH_WARNING_OFFSET_MINUTES,
    10,
  );

  const login = useCallback(() => {
    if (cis2Enabled) {
      signInWithRedirect({
        provider: {
          custom: cis2IdpName,
        },
      });
    } else {
      signInWithRedirect();
    }
  }, []);

  const logout = useCallback(() => {
    signOut({
      global: true,
      oauth: {
        redirectUrl: callbackUrl,
      },
    });
  }, []);

  const onIdle = () => {
    logout();
  };

  const { reset, pause } = useIdleTimer({
    timeout: 15 * 60 * 1000,
    onIdle,
    debounce: 500,
    disabled: !isSignedIn,
  });

  useEffect(() => {
    if (isSignedIn) {
      reset();
    } else {
      pause();
    }
  }, [isSignedIn, reset, pause]);

  const getUser = async () => {
    try {
      const authSession = await fetchAuthSession({ forceRefresh: true });

      const newAccessToken = authSession.tokens?.accessToken;
      const newIdToken = authSession.tokens?.idToken;

      if (newAccessToken && newIdToken) {
        setAccessToken(newAccessToken);
        setIdToken(newIdToken);
        setIsSignedIn(true);
        const currentUser = await getCurrentUser();
        setUser(currentUser);
      } else {
        setIsSignedIn(false);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!idToken) {
      setHasCognitoGroup(false);
      return;
    }

    if (!idToken.payload['cognito:groups']) {
      setHasCognitoGroup(false);
      return;
    }

    const cognitoGroups = idToken.payload['cognito:groups'] as string[];

    // Cognito automatically assigns new users to a group with a name ending "cis2-idp"
    // We do not want to include this when checking for a group
    if (cognitoGroups.filter((group) => !group.endsWith('cis2-idp')).length === 0) {
      setHasCognitoGroup(false);
      return;
    }

    setHasCognitoGroup(true);
  }, [idToken]);

  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect':
          getUser();
          break;
        case 'signInWithRedirect_failure':
          setError('An error has occurred during the OAuth flow.');
          break;
        default:
          setError('unknown event type');
      }
    });

    getUser();

    return unsubscribe;
  }, []);

  useEffect(() => {
    const authStartTime = accessToken?.payload?.auth_time as number | undefined;

    if (!authStartTime) {
      return undefined;
    }

    const authExpiryTime = DateTime.fromSeconds(authStartTime).plus({
      hours: 1,
    });
    const msUntilExpiry = authExpiryTime.diffNow().toMillis();

    if (msUntilExpiry <= 0) {
      logout();
      return undefined;
    }

    const expiryTimer = setTimeout(logout, msUntilExpiry);

    const authWarningTime = DateTime.fromSeconds(authStartTime).plus({
      minutes: warningOffsetMinutes,
    });
    const msUntilWarning = authWarningTime.diffNow().toMillis();

    const warningTimer = setTimeout(() => {
      setCis2TimeOutWarning(true);
    }, msUntilWarning);

    return () => {
      clearTimeout(expiryTimer);
      clearTimeout(warningTimer);
    };
  }, [accessToken, logout, warningOffsetMinutes]);

  const memo = useMemo(
    () => ({
      login,
      logout,
      user,
      error,
      tokens: {
        accessToken,
        idToken,
      },
      cis2TimeOutWarning,
    }),
    [login, logout, user, error, accessToken, idToken, cis2TimeOutWarning],
  );

  if (isLoading) {
    return (
      <AuthContext.Provider
        value={memo}
      >
        <SkipLink />
        <HomeHeader />
        <Container>
          <div className="nhsuk-u-margin-top-6">
            <ProgressIndicator />
          </div>
        </Container>
        <PageFooter />
      </AuthContext.Provider>
    );
  }

  if (!isSignedIn) {
    if (window.location.pathname !== '/' && isValidInternalPath(window.location.pathname)) {
      sessionStorage.setItem(INTENDED_URL, window.location.pathname);
    }

    return (
      <AuthContext.Provider
        value={memo}
      >
        <LandingPage />
      </AuthContext.Provider>
    );
  }

  if (isSignedIn && !hasCognitoGroup) {
    return (
      <AuthContext.Provider
        value={memo}
      >
        <NoGroupPage />
      </AuthContext.Provider>
    );
  }

  return (
    <AuthContext.Provider
      value={memo}
    >
      {children}
    </AuthContext.Provider>
  );
}
